import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

const DEBOUNCE_MS = 500;

export type ProjectsColumnSearchContextValue = {
  scopeId: string | null;
  searchInFields: string[];
  searchInput: string;
  debouncedColumnSearch: string;
  beginColumnSearchScope: (scopeId: string, searchInFields: string[]) => void;
  setColumnSearchInput: (value: string) => void;
  clearColumnSearchText: () => void;
  clearColumnSearch: () => void;
};

const ProjectsColumnSearchContext = createContext<ProjectsColumnSearchContextValue | null>(null);

export function ProjectsColumnSearchProvider({ children }: { children: ReactNode }) {
  const [scopeId, setScopeId] = useState<string | null>(null);
  const [searchInFields, setSearchInFields] = useState<string[]>([]);
  const [searchInput, setSearchInputState] = useState('');
  const [debouncedColumnSearch, setDebouncedColumnSearch] = useState('');
  const prevScopeRef = useRef<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedColumnSearch(searchInput.trim());
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const beginColumnSearchScope = useCallback((nextScopeId: string, fields: string[]) => {
    setScopeId(nextScopeId);
    setSearchInFields(fields);
    if (prevScopeRef.current !== nextScopeId) {
      setSearchInputState('');
      setDebouncedColumnSearch('');
    }
    prevScopeRef.current = nextScopeId;
  }, []);

  const setColumnSearchInput = useCallback((value: string) => {
    setSearchInputState(value);
  }, []);

  const clearColumnSearchText = useCallback(() => {
    setSearchInputState('');
    setDebouncedColumnSearch('');
  }, []);

  const clearColumnSearch = useCallback(() => {
    setScopeId(null);
    setSearchInFields([]);
    setSearchInputState('');
    setDebouncedColumnSearch('');
    prevScopeRef.current = null;
  }, []);

  const value = useMemo(
    () => ({
      scopeId,
      searchInFields,
      searchInput,
      debouncedColumnSearch,
      beginColumnSearchScope,
      setColumnSearchInput,
      clearColumnSearchText,
      clearColumnSearch,
    }),
    [
      scopeId,
      searchInFields,
      searchInput,
      debouncedColumnSearch,
      beginColumnSearchScope,
      setColumnSearchInput,
      clearColumnSearchText,
      clearColumnSearch,
    ]
  );

  return (
    <ProjectsColumnSearchContext.Provider value={value}>{children}</ProjectsColumnSearchContext.Provider>
  );
}

export function useProjectsColumnSearch(): ProjectsColumnSearchContextValue {
  const ctx = useContext(ProjectsColumnSearchContext);
  if (!ctx) {
    throw new Error('useProjectsColumnSearch must be used within ProjectsColumnSearchProvider');
  }
  return ctx;
}
